import { request } from './client';

/// Hardware capabilities a device reported at registration, echoed
/// back from the server's `attributes` column. Stored verbatim, so the
/// keys are snake_case to match what the client sent. All fields are
/// optional on the read side — older clients never reported them, and a
/// partial blob shouldn't break rendering.
export interface DeviceCapabilities {
  os?: string;
  os_version?: string;
  /// Chip slug, e.g. `"m2_pro"` or `"intel"`.
  chip?: string;
  chip_generation?: string;
  gpu_cores?: number;
  neural_engine?: boolean;
  total_memory_gb?: number;
  app_version?: string;
}

/// One device on the authenticated account, as returned by
/// `GET /account/devices`. Mirrors the server's `DeviceResponse` —
/// see [`apps/server/src/api/endpoints/responses.rs`].
export interface DeviceListItem {
  id: string;
  name: string;
  /// Server-supplied free-text label (`"desktop"`, `"mobile"`, …). The
  /// UI maps known values to a friendly platform name; anything else
  /// falls through to a generic device glyph.
  device_type: string;
  /// `"active"` for rows the server still considers live. Deactivated
  /// devices are filtered out by the list endpoint, so this is
  /// effectively a passive sanity-check today.
  status: string;
  /// Most recent successful sync wall-clock time (ISO-8601), or
  /// `null` if the device has never synced.
  last_sync_at: string | null;
  /// First registered. Used as the "first seen" timestamp.
  created_at: string;
  /// Push transport for this device: `"apns"`, `"fcm"`, or absent
  /// when the device has never registered a push token.
  push_platform?: string;
  /// `true` when this row is the device making the request. Older
  /// servers may omit the field; default to false for tolerance.
  current?: boolean;
  /// Server-derived trust signal: the device completed encryption bootstrap
  /// and at least one authenticated sync.
  paired?: boolean;
  /// Reported hardware capabilities, absent when the device never
  /// reported them. The UI renders a one-line summary when present.
  attributes?: DeviceCapabilities;
}

/// Envelope around the server's standard `ListResponse<T>`. Only
/// `items` is consumed by the UI; pagination fields ignored.
interface DeviceListResponse {
  items: DeviceListItem[];
}

export function listDevices(): Promise<DeviceListItem[]> {
  return request<DeviceListResponse>('/account/devices').then((res) => res.items);
}

/// Soft-deactivates a device and fires a `device_removed` silent push
/// at it so the local data is wiped on next wake. Server returns 204.
export function deactivateDevice(deviceId: string): Promise<void> {
  return request<void>(`/account/devices/${encodeURIComponent(deviceId)}`, {
    method: 'DELETE',
  });
}
